import { Spinner } from '@tribal-worldwide-london/ui-component-library-vwg';
import {
  DesignTokenSizeEnum,
  Text,
  TokenTextAppearance,
  styled,
} from '@volkswagen-onehub/components-core';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { useTribalContext } from '@/context/tribal';
import useAppNavigate, { PATHS } from '@/hooks/useAppNavigate';
import useGlobalStore, {
  Configurations,
  GlobalStoreStateConfiguration,
} from '@/stores/globalStore';
import { throwJsonAction } from '../actions';
import { Box } from '../components';
import {
  AppLoaderData,
  appLoader,
  configurationLoader,
  pdbLoader,
  productLoader,
  stockLoader,
} from '../loaders';
import { NAVIGATION_HEIGHT } from '../constants/navigation';
import { performVWBasicGenericFeatureAppLoad } from '@/analytics/events';
import { getFirstBuildableConfiguration, isAllNotBuildable, isNotBuildable } from '../utils/storeUtils';



const ScreenWrapper = styled.div(
  ({ theme }) => `
  display: flex;
  height: calc(100vh - ${NAVIGATION_HEIGHT}px);
  width: 100vw;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 1) 0%,
    ${theme.colors.border.divider} 55%
  );
`);

const Welcome = () => {
  const {
    productService,
    stockService,
    configurationService,
    pdbService,
    retailerService,
    contentManager,
    analyticsBuilder,
    analyticsManager,
  } = useTribalContext();
  const store = useGlobalStore();
  const params = useParams<{ pdbId: string }>();
  const navigate = useAppNavigate();

  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [appDataLoaded, setAppDataLoaded] = useState(false);
  const [pdbDataLoaded, setPdbDataLoaded] = useState(false);
  const [pageDataLoaded, setPageDataLoaded] = useState(false);


  const globalStoreUnsub = useGlobalStore.subscribe(
    (state) => {

      // Checks that initial data is loaded for expired scenario
      if(state.app.pdbId) {
        setInitialDataLoaded(true)
      }

      // -------------------------------------------------------- //

      // Checks that appData and configurations data have been loaded
      // Enables pdbQuery to run, which needs both
      // Assumes appData will always have a pdbId
      if (state.app.pdbId
        && state.app.configurations
        //probably don't need this check but we'll keep it in just in case
        && state.app.configurations[0].fullUrl
      ) {
        setAppDataLoaded(true)
      }
      // -------------------------------------------------------- //
      //Checks that there is pdb data in the configurations arr
      // Enables pageData query to run
      let pdbDataExists = state.app.configurations?.every((configuration) =>   configuration.pdbData);

      

      if (pdbDataExists && appDataLoaded) {
        setPdbDataLoaded(true);
      }
      // -------------------------------------------------------- //
      //Checks that there is product and stock/config data in all of the configurations
      // Enables pageData query to run
      let pageDataExists = state.app.configurations?.every((configuration) =>  isNotBuildable(configuration.pdbData) || 
       ( configuration.stockData ||  configuration.configData)
          // && configuration.productData - could potential be empty
      );
      if (pageDataExists && pdbDataLoaded) {
        setPageDataLoaded(true);
      }
    }
  )

  const {
    app,
    app: { configurations, pdbId, retailers },
    addConfigurationDataByKey,
  } = useGlobalStore(({ app, addConfigurationDataByKey }) => ({
    app,
    addConfigurationDataByKey,
  }));

  const { isLoading: isAppLoading, data } = useQuery<AppLoaderData | undefined>(
    'appData',
    () =>
      appLoader({ pdbService, retailerService }, { params }, contentManager),
    {
      onSuccess: (data: AppLoaderData | undefined) => {
        if (data?.appData && data?.retailersData) {
          store.setAppData(data.appData);
          store.setAppRetailersData(data.retailersData);
        }
      },
      useErrorBoundary: true,
    }
  );

  useQuery(
    'pdbData',
    () =>
      pdbLoader(
        { pdbService },
        // @ts-ignore: Query can only run when pbdId exists
        { params: { pdbId, configurations } },
        contentManager
      ),
    {
      onSuccess: (pdbData) => {
        addConfigurationDataByKey(pdbData, 'fullUrl', 'pdbData');
      },
      useErrorBoundary: true,
      enabled: appDataLoaded,
    }
  );


  useQuery(
    'pageData',
    () =>
      Promise.all([
        stockLoader(
          { stockService, configurationService },
          { params: { configurations } },
          contentManager
        ),
        configurationLoader(
          { configurationService },
          { params: { configurations } },
          contentManager
        ),
        productLoader(
          { productService },
          { params: { configurations } },
          contentManager
        ),
      ]),
    {
      onSuccess: (data: Configurations[]) => {
        addConfigurationDataByKey(data[0], 'orderNumber', 'stockData');
        addConfigurationDataByKey(data[1], 'marketingShortCode', 'configData');
        addConfigurationDataByKey(data[2], 'fullUrl', 'productData');
      },
      enabled: pdbDataLoaded,
      useErrorBoundary: true,
    }
  );


  useEffect(() => {
    let expired = dayjs().isAfter(dayjs(data?.appData?.pdbExpiresAt)) && !data?.appData?.status?.valid;

    // Expired - status invalid, pdbExpiresAt has passed
    if (initialDataLoaded && expired) {
      navigate(PATHS.expired);
    }

    else if (appDataLoaded && pdbDataLoaded && pageDataLoaded) {

      // Expired - all unbuildable
      if (isAllNotBuildable(app.configurations)){
        navigate(PATHS.expired);
      }

      // Valid
      else {
        performVWBasicGenericFeatureAppLoad({
          analyticsBuilder,
          analyticsManager,
          configurations,
          retailers,
        });

        const firstBuildableConfigurationUrl =
          getFirstBuildableConfiguration(configurations).fullUrl;

        navigate(firstBuildableConfigurationUrl);
      }
      globalStoreUnsub();
    }

  }, [initialDataLoaded, appDataLoaded, pdbDataLoaded, pageDataLoaded, app.configurations, data?.appData]);



  return (
    <ScreenWrapper>
      {!isAppLoading && (
        <>
          <Box
            as={Text}
            mb={DesignTokenSizeEnum.dynamic0020}
            appearance={TokenTextAppearance.headline300}
            bold
          >
            {contentManager.get('loading-page-greeting-text')}{' '}
            {app.message?.parameters?.customerFirstName || ''}
          </Box>
          <Box
            as={Text}
            mb={DesignTokenSizeEnum.dynamic0130}
            pt={DesignTokenSizeEnum.static150}
            appearance={TokenTextAppearance.headline400}
          >
            {contentManager.get('loading-page-welcome-text-normal')} <br />
            <strong>
              {contentManager.get('loading-page-welcome-text-bold')}
            </strong>
          </Box>
        </>
      )}
      <Box height="60px" width="60px">
        <Spinner />
      </Box>
    </ScreenWrapper>
  );
};

export default Welcome;
